import React, { useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { X, Upload, Sparkles, Loader2 } from "lucide-react";

export default function UploadDialog({ open, onClose, entityType, entityId, companyId, categories, onUploaded }) {
  const [categoria, setCategoria] = useState(categories[0]);
  const [customCategoria, setCustomCategoria] = useState("");
  const [nome, setNome] = useState("");
  const [dataEmissione, setDataEmissione] = useState("");
  const [dataScadenza, setDataScadenza] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  if (!open) return null;

  const reset = () => {
    setCategoria(categories[0]); setCustomCategoria(""); setNome(""); setDataEmissione("");
    setDataScadenza(""); setNote(""); setFile(null);
  };

  const runAI = async () => {
    if (!file) { toast.error("Seleziona prima un file"); return; }
    setAiBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/documents/parse", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
      // Try to map returned categoria into existing options
      if (data.categoria) {
        const match = categories.find(c => c.toLowerCase() === data.categoria.toLowerCase() || c.toLowerCase().includes(data.categoria.toLowerCase()));
        if (match) setCategoria(match);
        else { setCategoria("__custom__"); setCustomCategoria(data.categoria); }
      }
      if (data.nome) setNome(data.nome);
      if (data.data_emissione) setDataEmissione(data.data_emissione);
      if (data.data_scadenza) setDataScadenza(data.data_scadenza);
      const notaExtra = [data.soggetto && `Soggetto: ${data.soggetto}`, data.note].filter(Boolean).join(" · ");
      if (notaExtra) setNote(notaExtra);
      toast.success("Dati letti automaticamente. Verifica e conferma.");
    } catch (err) {
      toast.error("Impossibile leggere il documento con AI");
    } finally { setAiBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Seleziona un file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("entity_type", entityType);
      fd.append("entity_id", entityId);
      fd.append("company_id", companyId);
      const finalCat = categoria === "__custom__" ? customCategoria : categoria;
      fd.append("categoria", finalCat);
      fd.append("nome", nome || finalCat);
      fd.append("data_emissione", dataEmissione);
      fd.append("data_scadenza", dataScadenza);
      fd.append("note", note);
      fd.append("file", file);
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento caricato");
      reset();
      onUploaded && onUploaded();
      onClose();
    } catch (err) {
      toast.error("Errore caricamento");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-slate-200 shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="font-display font-semibold text-lg">Carica documento</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700">File</label>
            <label className="dropzone mt-1 block">
              <input type="file" onChange={(e) => setFile(e.target.files[0])} className="hidden" data-testid="doc-file-input" />
              <div className="flex flex-col items-center gap-2 text-slate-500">
                <Upload size={24} />
                <span className="text-sm">{file ? file.name : "Clicca per selezionare file (PDF o immagine)"}</span>
              </div>
            </label>
            {file && (
              <button type="button" onClick={runAI} disabled={aiBusy} data-testid="doc-ai-parse"
                className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-700 to-blue-900 hover:from-blue-800 hover:to-slate-900 text-white text-sm font-medium py-2 rounded-lg disabled:opacity-60 transition">
                {aiBusy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                {aiBusy ? "Analisi AI in corso…" : "Compila automaticamente con AI"}
              </button>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700">Categoria</label>
            <select value={categoria} onChange={(e) => setCategoria(e.target.value)} data-testid="doc-categoria"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg">
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              <option value="__custom__">+ Nuova categoria personalizzata</option>
            </select>
            {categoria === "__custom__" && (
              <input placeholder="Nome nuova categoria" value={customCategoria} onChange={(e) => setCustomCategoria(e.target.value)}
                className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-lg" />
            )}
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Nome documento</label>
            <input value={nome} onChange={(e) => setNome(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700">Data emissione</label>
              <input type="date" value={dataEmissione} onChange={(e) => setDataEmissione(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Data scadenza</label>
              <input type="date" value={dataScadenza} onChange={(e) => setDataScadenza(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Note</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-slate-600 hover:text-slate-900">Annulla</button>
            <button type="submit" disabled={busy} data-testid="doc-upload-submit"
              className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg disabled:opacity-60">
              {busy ? "Caricamento…" : "Carica"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
